import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { MathJaxContext } from 'better-react-mathjax';
import axios from 'axios';

import googleSearchService from './googleSearchService';
import ResourceList from './ResourceList';
import SynthesisCard from './SynthesisCard';
import ObjectiveCard from './ObjectiveCard';

// Composant de squelette pour la synthèse
function SynthesisCardSkeleton() {
  return (
    <div className="mb-6 rounded-xl overflow-hidden shadow-lg border border-gray-200 animate-pulse">
      <div className="bg-gradient-to-r from-indigo-500/50 via-purple-500/50 to-pink-500/50 p-4">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-white/30 rounded-full" />
          <div className="h-6 bg-white/30 rounded w-48" />
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-5/6" />
      </div>
    </div>
  );
}

// Composant de squelette pour la liste des ressources
function ResourceListSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="p-6 h-full flex flex-col bg-white rounded-xl shadow border border-gray-100 animate-pulse">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 rounded" />
              <div className="w-24 h-6 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-16 h-6 bg-gray-200 rounded-full" />
              <div className="w-20 h-6 bg-gray-200 rounded-full" />
            </div>
          </div>
          <div className="mb-4">
            <div className="w-32 h-6 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2 flex-grow">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function InspirationSpace() {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  // États pour les données
  const [competence, setCompetence] = useState(null);
  const [resources, setResources] = useState([]);
  const [savedResources, setSavedResources] = useState([]);
  
  // États pour le chargement et les erreurs
  const [competenceLoading, setCompetenceLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État pour le statut du chargement progressif
  const [loadingStatus, setLoadingStatus] = useState({
    competence: 'loading',  // 'loading' | 'success' | 'error'
    synthesis: 'loading',   // 'loading' | 'success' | 'error'
    resources: 'loading'    // 'loading' | 'success' | 'error'
  });

  const handleAuthError = () => {
    navigate('/login', {
      state: {
        redirectUrl: `/competence/${competenceId}`,
        message: 'Veuillez vous connecter pour accéder à cette ressource',
      },
    });
  };

  useEffect(() => {
    const fetchCompetenceAndResources = async () => {
      if (!competenceId) {
        setError('ID de compétence manquant');
        setLoadingStatus(prev => ({ ...prev, competence: 'error' }));
        return;
      }

      try {
        const user = auth.currentUser;
        if (!user) {
          handleAuthError();
          return;
        }

        const token = await user.getIdToken(true);

        // 1. Récupération de la compétence
        setLoadingStatus(prev => ({ ...prev, competence: 'loading' }));
        const competenceResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getcompetence/${competenceId}`,
          {
            params: { userId: user.uid },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!competenceResponse.data) {
          setError('Compétence non trouvée');
          setLoadingStatus(prev => ({ ...prev, competence: 'error' }));
          return;
        }

        const competenceData = competenceResponse.data;
        setCompetence(competenceData);
        setLoadingStatus(prev => ({ ...prev, competence: 'success' }));

        const userCountry = "FR"; 
        const userLanguage = "fr"; 
        
        // 2. Recherche des ressources éducatives
        setLoadingStatus(prev => ({ ...prev, resources: 'loading', synthesis: 'loading' }));
        const searchResults = await googleSearchService.searchEducationalContent(
          competenceData,
          user.uid,
          token,
          userLanguage || 'fr',
          userCountry || 'FR'
        );

        if (!searchResults || searchResults.length === 0) {
          setError('Aucune ressource trouvée');
          setLoadingStatus(prev => ({ ...prev, resources: 'error', synthesis: 'error' }));
        } else {
          setResources(searchResults);
          setLoadingStatus(prev => ({ ...prev, resources: 'success', synthesis: 'success' }));
        }

      } catch (err) {
        console.error('Erreur:', err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          handleAuthError();
        } else if (err.response?.status === 404) {
          setError('Compétence introuvable');
        } else {
          setError(err.message || 'Erreur lors de la récupération des données');
        }
        setLoadingStatus({
          competence: 'error',
          synthesis: 'error',
          resources: 'error'
        });
      }
    };

    fetchCompetenceAndResources();
  }, [competenceId, auth]);

  const toggleSaveResource = (resource) => {
    const isAlreadySaved = savedResources.some((r) => r.url === resource.url);
    if (isAlreadySaved) {
      setSavedResources((prev) => prev.filter((r) => r.url !== resource.url));
    } else {
      setSavedResources((prev) => [...prev, resource]);
    }
  };

  // Gestion de l'affichage des erreurs
  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Séparation de la synthèse et des autres ressources
  const synthesis = resources.find((r) => r.type === 'synthesis');
  const otherResources = resources.filter((r) => r.type !== 'synthesis')
    .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));

    return (
      <MathJaxContext>
        <Box p={3}>
          {/* Affichage immédiat de la carte objectif */}
          {competence && (
            <ObjectiveCard competence={competence} />
          )}
    
          {/* Synthèse avec Skeleton */}
          {loadingStatus.synthesis === 'loading' ? (
            <SynthesisCardSkeleton />
          ) : synthesis && (
            <SynthesisCard synthesis={synthesis} />
          )}
    
          {/* Liste des ressources avec Skeleton */}
          {loadingStatus.resources === 'loading' ? (
            <ResourceListSkeleton />
          ) : (
            <ResourceList
              resources={otherResources}
              savedResources={savedResources}
              toggleSaveResource={toggleSaveResource}
            />
          )}
    
          {/* Ressources sauvegardées */}
          {savedResources.length > 0 && (
            <Box mt={5} p={2} bgcolor="rgb(240 248 255)">
              <Typography variant="h6" mb={2}>
                Ressources sauvegardées ({savedResources.length})
              </Typography>
              {savedResources.map((resource) => (
                <Typography key={resource.url} variant="body2">
                  • {resource.title}
                </Typography>
              ))}
            </Box>
          )}
    
          {/* Affichage des erreurs */}
          {error && (
            <Box p={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
        </Box>
      </MathJaxContext>
    );
  }